/* XENGINEERS. Hero product cut-outs.
   The featured products in the home hero are studio photos on a white backdrop. On the dark hero a white square
   reads as a hole in the page, so this lifts each product off its backdrop in the browser and stands it on the
   dark field instead:
   - a flood fill from the photo's border finds the backdrop and the soft grey shadows that touch it, so whites
     inside the product (printed logos, labels, the flag patch) are never reached and stay solid;
   - large pure white pockets enclosed by the product (the hole of a ring) are backdrop too and are cleared;
   - inside the backdrop, colour to alpha turns grey shadows into see-through dark shadows, not grey halos;
   - the pixel or two where product and backdrop blend are separated again, then feathered by about a pixel;
   - a part the photographer cropped at the frame (a strap running off the top) fades out instead of ending in a line;
   - every product is framed the same way: centred, standing on one baseline at 90% of its frame.
   A photo whose border is not mostly white (a lifestyle shot, a coloured backdrop) is left as it is, and so is any
   photo the browser will not let a script read. Until a photo is settled the stylesheet keeps it hidden, so the
   white square never flashes. The result replaces the photo through a blob URL; the link and alt text stay. */
(() => {
  'use strict';
  const WRAP = '.xe-studio-hero__product-image';
  // Framing, as shares of the frame: products share a baseline, a width limit and a little headroom.
  const BASELINE = .9, FIT_WIDTH = .9, HEADROOM = .06, MAX_SCALE = 1.3;
  // Keying. "Lift" is how far a pixel stands out from the backdrop: 0 is the backdrop itself, 1 is black.
  const CLEAR = .07;     // lift below which a pixel is backdrop whatever surrounds it (paper texture, compression noise)
  const SHADOW = .74;    // the darkest a smooth grey may be and still count as a shadow on the backdrop
  const STEP = 60;       // brightness change across two pixels (0 to 255) above which a pixel is an edge, not a shadow
  const LEAK = .1;       // share of the photo in mid grey shadow above which the fill has run into a grey product
  const STEP_STRICT = 24; // the edge test used when filling again after a leak
  const POCKET = .003;   // enclosed pure white pockets larger than this share of the photo are backdrop
  const FADE = .1;       // share of the frame over which a product cropped by the photo's edge fades out
  const SLOW = 2500;     // ms after the photo has loaded before the plain photo is shown instead
  const FAILSAFE = 4000; // the stylesheet shows the plain photo by itself at 4.5 s; later than this, leave it on screen
  const blobs = new Map(), busy = new WeakSet();

  // Keying runs in a few short steps with a pause between them, so a tap or a scroll is never kept waiting.
  const pause = () => (window.scheduler?.yield ? window.scheduler.yield() : new Promise(resolve => setTimeout(resolve, 0)));
  const hover = matchMedia('(hover: hover)');
  const clamp = value => (value < 0 ? 0 : value > 255 ? 255 : value);
  const smooth = t => (t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t));
  const canvas = (width, height) => Object.assign(document.createElement('canvas'), { width, height });
  const median = list => list.sort((a, b) => a - b)[list.length >> 1];

  /* An image whose pixels a script may read. A same-origin photo comes straight from the memory cache. A photo on
     another domain is asked for again with CORS; a server that does not allow that leaves the plain photo. */
  async function readable(img) {
    const src = img.currentSrc || img.src;
    const probe = new Image();
    if (!/^(blob|data):/.test(src) && new URL(src, location.href).origin !== location.origin) probe.crossOrigin = 'anonymous';
    probe.decoding = 'async';
    probe.src = src;
    try { await probe.decode(); } catch (_) { return null; }
    return probe.naturalWidth ? probe : null;
  }

  function loaded(img) {
    if (img.complete) return img.naturalWidth ? Promise.resolve() : Promise.reject(new Error('photo'));
    return new Promise((resolve, reject) => {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', reject, { once: true });
    });
  }

  /* The cut-out itself. Returns a canvas, or a word for why the photo is better left as it is:
     scene (no white backdrop), locked (pixels may not be read), broken (no picture). */
  async function key(source, boxWidth, boxHeight) {
    const naturalWidth = source.naturalWidth || source.width, naturalHeight = source.naturalHeight || source.height;
    if (!naturalWidth || !naturalHeight) return 'broken';
    // Work at the size the photo is shown at (with room for the hover zoom where there is one), never above the file's own size.
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const target = Math.max(240, Math.ceil(Math.max(boxWidth, boxHeight) * dpr * (hover.matches ? 1.1 : 1)));
    const shrink = Math.min(1, target / Math.max(naturalWidth, naturalHeight));
    const W = Math.round(naturalWidth * shrink), H = Math.round(naturalHeight * shrink), n = W * H;
    if (W < 16 || H < 16) return 'scene';
    const work = canvas(W, H), context = work.getContext('2d', { willReadFrequently: true });
    context.imageSmoothingQuality = 'high';
    context.drawImage(source, 0, 0, W, H);
    let image;
    try { image = context.getImageData(0, 0, W, H); } catch (_) { return 'locked'; }
    const px = image.data;

    // 1. The backdrop: most of the border must be one near-white, near-grey colour. A photo that is already
    //    transparent at its border is a cut-out already and only needs framing.
    const edge = new Int32Array(2 * (W + H) - 4);
    let edges = 0, clear = 0;
    for (let x = 0; x < W; x++) { edge[edges++] = x; edge[edges++] = n - W + x; }
    for (let y = 1; y < H - 1; y++) { edge[edges++] = y * W; edge[edges++] = y * W + W - 1; }
    const reds = [], greens = [], blues = [];
    for (let k = 0; k < edges; k++) {
      const o = edge[k] * 4, r = px[o], g = px[o + 1], b = px[o + 2], low = Math.min(r, g, b);
      if (px[o + 3] < 16) clear++;
      else if (px[o + 3] > 250 && low >= 214 && Math.max(r, g, b) - low <= 20) { reds.push(r); greens.push(g); blues.push(b); }
    }
    if (clear >= edges * .6) return frame(work, image, W, H, boxWidth, boxHeight);
    if (reds.length < edges * .6) return 'scene';
    const BR = median(reds), BG = median(greens), BB = median(blues);
    if (Math.min(BR, BG, BB) < 226) return 'scene';

    // 2. Lift of every pixel against the backdrop (colour to alpha), and brightness for the edge test.
    const lift = new Float32Array(n), light = new Uint8Array(n), open = new Uint8Array(n);
    for (let i = 0, o = 0; i < n; i++, o += 4) {
      const r = px[o], g = px[o + 1], b = px[o + 2];
      lift[i] = Math.max(r < BR ? (BR - r) / BR : 0, g < BG ? (BG - g) / BG : 0, b < BB ? (BB - b) / BB : 0);
      light[i] = (r * 77 + g * 151 + b * 28) >> 8;
    }
    // A pixel the fill may cross: grey rather than coloured, and either almost the backdrop or a shadow (no sharp
    // change of brightness around it). Coloured, dark or sharply edged pixels belong to the product.
    const passable = (shadow, step) => {
      open.fill(0);
      for (let i = 0, o = 0; i < n; i++, o += 4) {
        const a = lift[i];
        if (a > shadow) continue;
        const r = px[o], g = px[o + 1], b = px[o + 2], low = Math.min(r, g, b);
        if (Math.max(r, g, b) - low > 10 + (255 - low) * .25) continue;
        if (a <= CLEAR) { open[i] = 1; continue; }
        const x = i % W, y = (i - x) / W;
        const across = x > 0 && x < W - 1 ? Math.abs(light[i + 1] - light[i - 1]) : 0;
        const down = y > 0 && y < H - 1 ? Math.abs(light[i + W] - light[i - W]) : 0;
        if (across <= step && down <= step) open[i] = 1;
      }
    };
    passable(SHADOW, STEP);
    await pause();

    // 3. Flood fill from the border, then from large enclosed pockets of pure backdrop.
    const reach = new Uint8Array(n), stack = new Int32Array(n);
    let top = 0;
    const flood = () => {
      while (top) {
        const i = stack[--top], x = i % W;
        if (x > 0 && open[i - 1] && !reach[i - 1]) { reach[i - 1] = 1; stack[top++] = i - 1; }
        if (x < W - 1 && open[i + 1] && !reach[i + 1]) { reach[i + 1] = 1; stack[top++] = i + 1; }
        if (i >= W && open[i - W] && !reach[i - W]) { reach[i - W] = 1; stack[top++] = i - W; }
        if (i < n - W && open[i + W] && !reach[i + W]) { reach[i + W] = 1; stack[top++] = i + W; }
      }
    };
    const fill = () => { for (let k = 0; k < edges; k++) { const i = edge[k]; if (open[i] && !reach[i]) { reach[i] = 1; stack[top++] = i; } } flood(); };
    fill();
    // Real shadows are mostly light. When a large share of what the fill reached is mid grey, it has run into a grey
    // product standing in its own shadow (a grey band): fill again, taking only light, soft greys for shadow.
    let deep = 0;
    for (let i = 0; i < n; i++) if (reach[i] && lift[i] > .3) deep++;
    if (deep > n * LEAK) { reach.fill(0); passable(.3, STEP_STRICT); fill(); }
    // Pockets the fill could not reach. A large one is backdrop (the hole of a ring), and so is a mid-sized one of the
    // backdrop's exact flat white (gaps between buckles), or a small one walled off by only a thin line (the loops of
    // a strap). Other small or uneven ones are whites printed on the product (logos, labels, a flag) and stay.
    const pocket = new Int32Array(n), seen = new Uint8Array(n), large = Math.max(30, n * POCKET), middle = Math.max(12, n * POCKET / 8);
    const small = Math.max(6, Math.round(n * .00008)), wall = Math.max(2, Math.round(Math.min(W, H) * .012));
    const pure = i => open[i] && lift[i] <= .04 && !reach[i] && !seen[i];
    const thin = size => { // share of the pocket's rim within a wall's width of the backdrop
      let rim = 0, near = 0;
      for (let k = 0; k < size; k++) {
        const i = pocket[k], x = i % W, y = (i - x) / W;
        if (x > 0 && x < W - 1 && y > 0 && y < H - 1 && seen[i - 1] && seen[i + 1] && seen[i - W] && seen[i + W]) continue;
        rim++;
        for (let d = 1; d <= wall + 1; d++) {
          if ((x - d >= 0 && reach[i - d]) || (x + d < W && reach[i + d]) || (y - d >= 0 && reach[i - d * W]) || (y + d < H && reach[i + d * W])) { near++; break; }
        }
      }
      return near >= rim * .5;
    };
    for (let start = 0; start < n; start++) {
      if (!pure(start)) continue;
      let size = 0, head = 0, flat = 0;
      seen[start] = 1; pocket[size++] = start;
      while (head < size) {
        const i = pocket[head++], x = i % W;
        if (lift[i] <= .012) flat++;
        if (x > 0 && pure(i - 1)) { seen[i - 1] = 1; pocket[size++] = i - 1; }
        if (x < W - 1 && pure(i + 1)) { seen[i + 1] = 1; pocket[size++] = i + 1; }
        if (i >= W && pure(i - W)) { seen[i - W] = 1; pocket[size++] = i - W; }
        if (i < n - W && pure(i + W)) { seen[i + W] = 1; pocket[size++] = i + W; }
      }
      const even = flat >= size * .7;
      if (size < large && !(even && size >= middle) && !(flat >= size * .6 && size >= small && thin(size))) continue;
      for (let k = 0; k < size; k++) { reach[pocket[k]] = 1; stack[top++] = pocket[k]; }
      flood();
    }
    // Specks: a few grey pixels left stranded inside a shadow by compression noise belong to the shadow.
    const speck = Math.max(4, Math.round(n * .00012));
    seen.fill(0);
    for (let start = 0; start < n; start++) {
      if (reach[start] || seen[start]) continue;
      let size = 0, head = 0, grey = true;
      seen[start] = 1; pocket[size++] = start;
      while (head < size) {
        const i = pocket[head++], x = i % W, y = (i - x) / W, o = i * 4, low = Math.min(px[o], px[o + 1], px[o + 2]);
        if (Math.max(px[o], px[o + 1], px[o + 2]) - low > 10 + (255 - low) * .25) grey = false;
        for (let dy = -1; dy <= 1; dy++) {
          const yy = y + dy; if (yy < 0 || yy >= H) continue;
          for (let dx = -1; dx <= 1; dx++) {
            const xx = x + dx, j = yy * W + xx;
            if (xx >= 0 && xx < W && !reach[j] && !seen[j]) { seen[j] = 1; pocket[size++] = j; }
          }
        }
      }
      if (size <= speck && grey) for (let k = 0; k < size; k++) reach[pocket[k]] = 1;
    }
    await pause();

    // 4. Matte. Backdrop and shadows: colour to alpha, so a grey shadow becomes a see-through dark one.
    const alpha = new Float32Array(n);
    let solid = 0;
    for (let i = 0, o = 0; i < n; i++, o += 4) {
      if (!reach[i]) { alpha[i] = 1; continue; }
      const a = lift[i];
      alpha[i] = a <= .03 ? 0 : (a - .03) / .97;
      if (a < .004) { px[o] = px[o + 1] = px[o + 2] = 0; continue; }
      px[o] = clamp(BR - (BR - px[o]) / a); px[o + 1] = clamp(BG - (BG - px[o + 1]) / a); px[o + 2] = clamp(BB - (BB - px[o + 2]) / a);
    }
    // The product's outline: the first two pixels inside it are a blend of product and backdrop. Each is separated
    // against the nearest pixel fully inside the product (or the most solid pixel close by, on thin parts).
    const band = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      if (reach[i]) continue;
      const x = i % W;
      if ((x > 0 && reach[i - 1]) || (x < W - 1 && reach[i + 1]) || (i >= W && reach[i - W]) || (i < n - W && reach[i + W])) band[i] = 1;
    }
    for (let i = 0; i < n; i++) {
      if (reach[i] || band[i]) continue;
      const x = i % W;
      if ((x > 0 && band[i - 1] === 1) || (x < W - 1 && band[i + 1] === 1) || (i >= W && band[i - W] === 1) || (i < n - W && band[i + W] === 1)) band[i] = 2;
    }
    for (let i = 0; i < n; i++) {
      if (!band[i]) continue;
      const x = i % W, y = (i - x) / W, o = i * 4;
      let fr = 0, fg = 0, fb = 0, found = 0;
      for (let d = 1; d <= 3 && !found; d++) {
        for (let yy = y - d; yy <= y + d; yy++) {
          if (yy < 0 || yy >= H) continue;
          const ring = yy === y - d || yy === y + d;
          for (let xx = x - d; xx <= x + d; xx += ring ? 1 : 2 * d) {
            if (xx < 0 || xx >= W) continue;
            const j = yy * W + xx;
            if (reach[j] || band[j]) continue;
            fr += px[j * 4]; fg += px[j * 4 + 1]; fb += px[j * 4 + 2]; found++;
          }
        }
      }
      if (found) { fr /= found; fg /= found; fb /= found; }
      else {
        let best = i;
        for (let yy = Math.max(0, y - 2); yy <= Math.min(H - 1, y + 2); yy++) {
          for (let xx = Math.max(0, x - 2); xx <= Math.min(W - 1, x + 2); xx++) { const j = yy * W + xx; if (!reach[j] && lift[j] > lift[best]) best = j; }
        }
        fr = px[best * 4]; fg = px[best * 4 + 1]; fb = px[best * 4 + 2];
      }
      const dr = BR - fr, dg = BG - fg, db = BB - fb, span = dr * dr + dg * dg + db * db;
      if (span < 1600) continue; // the product is nearly as light as the backdrop here: keep the pixel solid
      let a = ((BR - px[o]) * dr + (BG - px[o + 1]) * dg + (BB - px[o + 2]) * db) / span;
      a = a < 0 ? 0 : a > 1 ? 1 : a;
      alpha[i] = a;
      const trust = Math.min(1, a * 2);
      if (a > .02) {
        px[o] = clamp((px[o] - (1 - a) * BR) / a) * trust + fr * (1 - trust);
        px[o + 1] = clamp((px[o + 1] - (1 - a) * BG) / a) * trust + fg * (1 - trust);
        px[o + 2] = clamp((px[o + 2] - (1 - a) * BB) / a) * trust + fb * (1 - trust);
      } else { px[o] = fr; px[o + 1] = fg; px[o + 2] = fb; }
    }
    // About a pixel of feathering along the outline.
    const soft = alpha.slice();
    for (let i = 0; i < n; i++) {
      if (band[i] !== 1) continue;
      const x = i % W;
      const around = (x > 0 ? alpha[i - 1] : alpha[i]) + (x < W - 1 ? alpha[i + 1] : alpha[i]) + (i >= W ? alpha[i - W] : alpha[i]) + (i < n - W ? alpha[i + W] : alpha[i]);
      soft[i] = alpha[i] * .5 + around * .125;
    }
    await pause();

    // 5. A product cropped by the photo's edge fades out towards that edge.
    const enough = Math.max(2, Math.round(Math.min(W, H) * .01)), fade = Math.max(4, Math.round(Math.min(W, H) * FADE));
    const count = (from, step, length) => { let c = 0; for (let k = 0; k < length; k++) if (soft[from + k * step] >= .5) c++; return c >= enough; };
    const touch = { top: count(0, 1, W), bottom: count(n - W, 1, W), left: count(0, W, H), right: count(W - 1, W, H) };
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        let f = 1;
        if (touch.top && y < fade) f *= smooth((y + .5) / fade);
        if (touch.bottom && H - 1 - y < fade) f *= smooth((H - .5 - y) / fade);
        if (touch.left && x < fade) f *= smooth((x + .5) / fade);
        if (touch.right && W - 1 - x < fade) f *= smooth((W - .5 - x) / fade);
        const i = y * W + x, a = soft[i] * f;
        px[i * 4 + 3] = Math.round(a * 255);
        soft[i] = a; if (a >= .5) solid++;
      }
    }
    // Nearly nothing left: this was not a product on a backdrop.
    if (solid < n * .01) return 'scene';
    await pause();
    return frame(work, image, W, H, boxWidth, boxHeight, touch);
  }

  /* Places the cut-out in a frame shaped like the box it is shown in: centred, standing on the baseline. A product
     that runs off the top of the photo (rings on their straps) keeps hanging from the top of the frame, and one that
     runs off the bottom fades out on the baseline. */
  function frame(work, image, W, H, boxWidth, boxHeight, touch = {}) {
    // The product's extent, found part by part. A small part on its own near the edge of the photo is a badge or a
    // sticker laid over the photo, not the product: it is left out of the cut-out.
    const px = image.data, n = W * H, part = new Int32Array(n), queue = new Int32Array(n), parts = [];
    for (let start = 0; start < n; start++) {
      if (px[start * 4 + 3] < 128 || part[start]) continue;
      const id = parts.length + 1;
      let size = 0, head = 0, x0 = W, y0 = H, x1 = -1, y1 = -1;
      part[start] = id; queue[size++] = start;
      while (head < size) {
        const i = queue[head++], x = i % W, y = (i - x) / W;
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
        for (let dy = -1; dy <= 1; dy++) {
          const yy = y + dy; if (yy < 0 || yy >= H) continue;
          for (let dx = -1; dx <= 1; dx++) {
            const xx = x + dx, j = yy * W + xx;
            if (xx >= 0 && xx < W && !part[j] && px[j * 4 + 3] >= 128) { part[j] = id; queue[size++] = j; }
          }
        }
      }
      parts.push({ id, size, x0, y0, x1, y1 });
    }
    if (!parts.length) return 'scene';
    const main = parts.reduce((a, b) => (b.size > a.size ? b : a)), near = Math.max(W, H) * .02;
    let x0 = W, y0 = H, x1 = -1, y1 = -1;
    for (const p of parts) {
      const touches = p.x1 >= main.x0 - near && p.x0 <= main.x1 + near && p.y1 >= main.y0 - near && p.y0 <= main.y1 + near;
      const fringe = p.x0 > W * .8 || p.x1 < W * .2 || p.y0 > H * .8 || p.y1 < H * .2;
      if (p.size < Math.max(4, main.size * .06) && !touches && fringe) {
        // Clear the part and its soft edge, without touching any other solid part.
        for (let y = Math.max(0, p.y0 - 3); y <= Math.min(H - 1, p.y1 + 3); y++) {
          for (let x = Math.max(0, p.x0 - 3); x <= Math.min(W - 1, p.x1 + 3); x++) { const i = y * W + x; if (!part[i] || part[i] === p.id) px[i * 4 + 3] = 0; }
        }
        continue;
      }
      x0 = Math.min(x0, p.x0); y0 = Math.min(y0, p.y0); x1 = Math.max(x1, p.x1); y1 = Math.max(y1, p.y1);
    }
    if (x1 < 0) return 'scene';
    // A side the product was cropped at counts from the photo's edge, so its fade stays whole.
    if (touch.top) y0 = 0;
    if (touch.bottom) y1 = H - 1;
    if (touch.left) x0 = 0;
    if (touch.right) x1 = W - 1;
    work.getContext('2d').putImageData(image, 0, 0);
    const size = Math.max(W, H), ratio = boxWidth > 0 && boxHeight > 0 ? boxWidth / boxHeight : 1;
    const outWidth = ratio >= 1 ? size : Math.round(size * ratio), outHeight = ratio >= 1 ? Math.round(size / ratio) : size;
    const ceiling = touch.top ? 0 : HEADROOM * outHeight;
    const scale = Math.min(FIT_WIDTH * outWidth / (x1 - x0 + 1), (BASELINE * outHeight - ceiling) / (y1 - y0 + 1), MAX_SCALE);
    const out = canvas(outWidth, outHeight), context = out.getContext('2d');
    context.imageSmoothingQuality = 'high';
    context.drawImage(work, (outWidth - scale * (x0 + x1 + 1)) / 2, BASELINE * outHeight - scale * (y1 + 1), W * scale, H * scale);
    out.span = Math.min(1, scale * (x1 - x0 + 1) / outWidth);
    work.width = work.height = 0;
    return out;
  }

  // Photos load side by side but are keyed one after another, so only one is ever being worked on.
  let lane = Promise.resolve();
  const queue = task => { const run = lane.then(task, task); lane = run.catch(() => {}); return run; };

  async function cut(wrap) {
    const img = wrap.querySelector('img');
    if (!img || busy.has(wrap) || ['done', 'off', 'photo'].includes(wrap.dataset.xeCut)) return;
    busy.add(wrap);
    // Hidden until settled, unless the stylesheet's own fallback has already put the plain photo on screen.
    if (!wrap.dataset.xeCut && performance.now() < FAILSAFE) wrap.dataset.xeCut = 'wait';
    let timer = 0, late = false;
    // Once the plain photo has been shown because keying was slow, it stays: no late swap in front of the shopper.
    const settle = state => { if (!late) wrap.dataset.xeCut = state; };
    try {
      await loaded(img);
      timer = setTimeout(() => { if (wrap.dataset.xeCut !== 'done') { wrap.dataset.xeCut = 'off'; late = true; } }, SLOW);
      const source = await readable(img);
      if (!source) return settle('off');
      const result = await queue(async () => {
        if (late) return 'late';
        const box = wrap.getBoundingClientRect(), start = performance.now();
        const output = await key(source, box.width, box.height);
        // Shows in the browser's performance panel as "xe-cutout <product>".
        try { performance.measure(`xe-cutout ${img.alt || ''}`.trim(), { start, end: performance.now() }); } catch (_) { /* Timing is optional. */ }
        return output;
      });
      if (result === 'late') return;
      if (typeof result === 'string') return settle(result === 'scene' ? 'photo' : 'off');
      const blob = await new Promise(resolve => result.toBlob(resolve, 'image/png'));
      if (!blob || late || !wrap.isConnected) return settle('off');
      const url = URL.createObjectURL(blob);
      blobs.set(wrap, url);
      img.removeAttribute('srcset'); img.removeAttribute('sizes'); img.src = url;
      await img.decode().catch(() => {});
      wrap.style.setProperty('--xe-cut-span', result.span.toFixed(3));
      result.width = result.height = 0;
      settle('done');
    } catch (_) {
      settle('off');
    } finally {
      clearTimeout(timer);
      busy.delete(wrap);
      // The row records how many photos became cut-outs (all, some, none), for any style that depends on the set.
      const row = wrap.closest('.xe-studio-hero__products');
      if (row) {
        const states = [...row.querySelectorAll(WRAP)].map(item => item.dataset.xeCut);
        if (!states.some(state => !state || state === 'wait')) row.dataset.xeCuts = states.every(state => state === 'done') ? 'all' : states.some(state => state === 'done') ? 'some' : 'none';
      }
    }
  }

  function setup(scope = document) {
    return Promise.all([...scope.querySelectorAll(WRAP)].map(cut));
  }
  function release(scope) {
    for (const [wrap, url] of blobs) if (scope.contains(wrap)) { URL.revokeObjectURL(url); blobs.delete(wrap); }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setup(), { once: true }); else setup();
  // The theme editor renders a section again whenever a product block changes.
  document.addEventListener('shopify:section:load', event => setup(event.target));
  document.addEventListener('shopify:section:unload', event => release(event.target));
  window.XECutout = { refresh: setup, key };
})();
